import React, { useState, useEffect } from 'react';
import { Mail, Save, Eye, RefreshCw, Code } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import DOMPurify from 'dompurify';

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/email-templates');
      setTemplates(r.data || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await api.patch(`/admin/email-templates/${editing.id}`, {
        subject: editing.subject,
        html_body: editing.html_body,
        text_body: editing.text_body,
      });
      toast.success('Template saved');
      setEditing(null);
      await fetchTemplates();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handlePreview = async (templateId) => {
    try {
      const r = await api.post('/admin/email-templates/preview', { template_id: templateId });
      setPreview(r.data);
    } catch { toast.error('Preview failed'); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Email Templates</h1>
          <p className="text-slate-300">Customize branded email notifications</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTemplates} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Template List */}
      <div className="space-y-4">
        {templates.map(tmpl => (
          <Card key={tmpl.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Mail className="h-4 w-4 text-red-400" />
                  {tmpl.name}
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePreview(tmpl.id)}>
                    <Eye className="h-4 w-4 mr-1" /> Preview
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing({ ...tmpl })}>
                    <Code className="h-4 w-4 mr-1" /> Edit
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-2">Subject: <code className="bg-[#131C3A] px-1 rounded">{tmpl.subject}</code></p>
              <div className="flex gap-1 flex-wrap">
                {(tmpl.variables || []).map(v => (
                  <Badge key={v} variant="outline" className="text-[10px]">{`{{${v}}}`}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Edit Template: {editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Subject Line</label>
                <Input value={editing.subject} onChange={e => setEditing({ ...editing, subject: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">HTML Body</label>
                <Textarea value={editing.html_body} onChange={e => setEditing({ ...editing, html_body: e.target.value })} rows={12} className="font-mono text-xs" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Plain Text Body</label>
                <Textarea value={editing.text_body} onChange={e => setEditing({ ...editing, text_body: e.target.value })} rows={5} className="font-mono text-xs" />
              </div>
              <div className="flex gap-1 flex-wrap">
                <span className="text-xs text-gray-500 mr-1">Variables:</span>
                {(editing.variables || []).map(v => (
                  <Badge key={v} variant="secondary" className="text-[10px] cursor-pointer" onClick={() => navigator.clipboard.writeText(`{{${v}}}`)}>
                    {`{{${v}}}`}
                  </Badge>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save Template'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="mt-2">
              <p className="text-sm mb-3"><strong>Subject:</strong> {preview.subject}</p>
              <Separator className="mb-3" />
              <div
                className="border rounded-lg overflow-hidden"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(preview.html, {
                    // Defense-in-depth: strip scripts, on*-handlers, javascript: URLs,
                    // and hostile SVG/MathML vectors even though preview.html comes
                    // from a trusted admin-only endpoint.
                    USE_PROFILES: { html: true },
                    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
                    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'formaction'],
                  }),
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;
